<template>
  <div class="gmx-editor" :class="{ 'error': error }">
    <QuillEditor
      v-model:content="content"
      :options="editorOptions"
      contentType="html"
      @update:content="handleUpdate"
      @textChange="handleTextChange"
    />
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script setup>
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { computed, defineProps, defineEmits, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  height: {
    type: String,
    default: '500px'
  }
});

const emit = defineEmits(['update:modelValue']);

const content = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  }
});

const editorOptions = {
  theme: 'snow',
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image']
    ]
  }
};

const handleUpdate = (content) => {
  if (content && content.html) {
    emit('update:modelValue', content.html);
  } else {
    emit('update:modelValue', content);
  }
};

const handleTextChange = (delta, oldDelta, source) => {
  if (source === 'user') {
    const editor = document.querySelector('.ql-editor');
    if (editor) {
      emit('update:modelValue', editor.innerHTML);
    }
  }
};

// 외부에서 modelValue가 변경될 때 에디터 내용 업데이트
watch(() => props.modelValue, (newValue) => {
  if (newValue !== content.value) {
    content.value = newValue;
  }
});
</script>

<style scoped lang="scss">
.gmx-editor {
  width: 100%;
  
  :deep(.ql-container) {
    height: calc(v-bind(height) - 42px) !important;
  }

  :deep(.ql-editor) {
    height: 100% !important;
    min-height: calc(v-bind(height) - 42px) !important;
    font-size: 14px;
    line-height: 1.6;

    p {
      margin-bottom: 1em;
    }

    strong {
      font-weight: bold;
    }

    em {
      font-style: italic;
    }

    h1, h2, h3, h4, h5, h6 {
      font-weight: bold;
      margin: 1em 0 0.5em;
    }

    h1 { font-size: 2em; }
    h2 { font-size: 1.5em; }
    h3 { font-size: 1.17em; }
    h4 { font-size: 1em; }
    h5 { font-size: 0.83em; }
    h6 { font-size: 0.67em; }

    ul, ol {
      padding-left: 2em;
      margin-bottom: 1em;
    }

    img {
      max-width: 100%;
      height: auto;
      margin: 1em 0;
    }

    blockquote {
      border-left: 4px solid #ccc;
      margin: 1em 0;
      padding-left: 16px;
    }

    code {
      background-color: #f4f4f4;
      padding: 2px 4px;
      border-radius: 3px;
    }
  }

  :deep(.ql-toolbar) {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background-color: #f8f9fa;
  }

  &.error {
    :deep(.ql-container) {
      border-color: var(--gmx-error-color);
    }
  }

  .error-message {
    color: var(--gmx-error-color);
    font-size: 12px;
    margin-top: 4px;
  }
}
</style> 